import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading } from "@/components/ui-kit";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, Crown, TrendingUp, Medal, Flame } from "lucide-react";

const MEDAL = { 1: "#F59E0B", 2: "#94A3B8", 3: "#B45309" };

function PodiumCard({ r, place, topImprover }) {
  const color = MEDAL[place];
  const heights = { 1: "h-28", 2: "h-20", 3: "h-16" };
  return (
    <div className={`flex flex-col items-center ${place === 1 ? "order-2" : place === 2 ? "order-1" : "order-3"}`}>
      <div className="relative">
        <Avatar className={`h-16 w-16 border-4 ${r.is_me ? "ring-2 ring-[#7C3AED] ring-offset-2" : ""}`} style={{ borderColor: color }}>
          <AvatarImage src={r.avatar_url || undefined} />
          <AvatarFallback className="bg-[#EDE9FE] font-bold text-[#5B21B6]">{r.name.split(" ").map((x) => x[0]).join("")}</AvatarFallback>
        </Avatar>
        {place === 1 && <Crown className="absolute -top-4 left-1/2 h-6 w-6 -translate-x-1/2 text-[#F59E0B]" fill="#F59E0B" />}
      </div>
      <div className="mt-2 max-w-[110px] truncate text-center text-sm font-bold text-[#1E1B2E]">{r.name}</div>
      <div className="font-heading text-xl font-extrabold" style={{ color }}>{r.average}%</div>
      {topImprover === r.id && <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#ECFDF5] px-2 py-0.5 text-[10px] font-bold text-[#059669]"><TrendingUp className="h-3 w-3" /> Top improver</span>}
      <div className={`mt-2 w-24 rounded-t-xl ${heights[place]}`} style={{ background: `linear-gradient(180deg, ${color}, ${color}88)` }}>
        <div className="pt-2 text-center font-heading text-2xl font-extrabold text-white">{place}</div>
      </div>
    </div>
  );
}

export default function Leaderboard() {
  const { user } = useAuth();
  const isTutor = user?.role === "tutor";
  const [prog, setProg] = useState(isTutor ? "jmss" : user?.program || "jmss");
  const [data, setData] = useState(null);

  useEffect(() => { setData(null); api.get("/leaderboard", { params: { program: prog } }).then((r) => setData(r.data)); }, [prog]);

  const top3 = data ? data.rows.slice(0, 3) : [];
  const rest = data ? data.rows.slice(3) : [];

  return (
    <div className="animate-fade-up">
      <PageHeader
        title="Class Leaderboard"
        subtitle={isTutor ? "See who's leading the class and who's improving fastest." : "See where you stand in your program — keep climbing!"}
        right={isTutor ? (
          <Select value={prog} onValueChange={setProg}>
            <SelectTrigger data-testid="lb-program-filter" className="h-10 w-56 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="jmss">JMSS Year 10</SelectItem>
              <SelectItem value="selective">Year 9 Selective</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EDE9FE] px-3 py-1.5 text-sm font-bold text-[#5B21B6]"><Flame className="h-4 w-4" /> {data?.rows?.find((r) => r.is_me)?.program_name || ""}</span>
        )}
      />

      {!data ? <Loading /> : data.rows.length === 0 ? (
        <div className="ember-card py-16 text-center text-sm text-[#948EAB]">No results in this program yet.</div>
      ) : (
        <>
          {top3.length > 0 && (
            <div className="ember-card mb-6 ember-gradient-soft p-8">
              <div className="flex items-end justify-center gap-4 sm:gap-8">
                {top3.map((r) => <PodiumCard key={r.id} r={r} place={r.rank} topImprover={data.top_improver} />)}
              </div>
            </div>
          )}

          <div className="ember-card overflow-hidden">
            <div className="border-b border-[#F1EEFA] p-4 font-heading font-bold text-[#1E1B2E]">Full ranking</div>
            <div className="divide-y divide-[#F1EEFA]">
              {data.rows.map((r) => (
                <div key={r.id} data-testid={`lb-row-${r.id}`}
                  className={`flex items-center gap-4 px-4 py-3 ${r.is_me ? "bg-[#F5F3FF]" : ""}`}>
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl font-heading text-sm font-extrabold"
                    style={{ background: MEDAL[r.rank] ? `${MEDAL[r.rank]}22` : "#F3F0FB", color: MEDAL[r.rank] || "#7C3AED" }}>
                    {r.rank <= 3 ? <Medal className="h-4 w-4" /> : r.rank}
                  </div>
                  <Avatar className="h-10 w-10"><AvatarImage src={r.avatar_url || undefined} /><AvatarFallback className="bg-[#EDE9FE] text-xs font-bold text-[#5B21B6]">{r.name.split(" ").map((x) => x[0]).join("")}</AvatarFallback></Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 truncate font-bold text-[#1E1B2E]">
                      {r.name}
                      {r.is_me && <span className="rounded-full bg-[#7C3AED] px-2 py-0.5 text-[10px] font-bold text-white">You</span>}
                      {data.top_improver === r.id && <span className="inline-flex items-center gap-1 rounded-full bg-[#ECFDF5] px-2 py-0.5 text-[10px] font-bold text-[#059669]"><TrendingUp className="h-3 w-3" /> +{r.improvement}%</span>}
                    </div>
                    <div className="text-xs text-[#948EAB]">{r.completed} assessment{r.completed === 1 ? "" : "s"} · best {r.best}%</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading text-lg font-extrabold text-[#5B21B6]">{r.average}%</div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-[#948EAB]">avg</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
